import { useEffect, useState } from "react";

/**
 * The two controls a settings screen is made of.
 *
 * Exported from this package, unlike the panels around them, and the distinction
 * is the one the index states: a *panel* is a screen, and a host that assembled
 * its own out of shared pieces could drift from the other host's. A *row* is a
 * label, a control and a unit — and the two hosts' settings screens are
 * genuinely different screens made of the same rows, because half of what the
 * desktop's overlay-defaults screen sets describes a floating window that a tab
 * does not have.
 *
 * What is worth sharing is not the layout, it is the commit rule: when a typed
 * value is sent, and when it is not. Duplicating that is how the two hosts end
 * up disagreeing about what happens to a half-typed number.
 */

/**
 * A labelled number field that commits on blur or Enter, and abandons the edit on
 * Escape.
 *
 * The commit rule is the whole reason this is not an ordinary input. The host
 * clamps what it is given, so committing per keystroke would rewrite "4" to the
 * minimum rate before the user had finished typing "40".
 */
export function SettingNumber({
  label,
  unit,
  value,
  min,
  max,
  onCommit,
}: {
  label: string;
  unit: string;
  value: number | string;
  min: number;
  max: number;
  onCommit: (raw: string) => void;
}): JSX.Element {
  const [text, setText] = useState(String(value));
  // Follow the value while the field is not being edited; `value` only changes
  // when the host has accepted something, which is exactly when the box should
  // agree with it.
  useEffect(() => setText(String(value)), [value]);

  return (
    <label className="settings-row">
      <span>{label}</span>
      <input
        type="number"
        value={text}
        min={min}
        max={max}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onCommit(text)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit(text);
          if (e.key === "Escape") setText(String(value));
        }}
      />
      <span className="settings-unit">{unit}</span>
    </label>
  );
}

/** A labelled slider, which needs no commit rule: every position it can be
 * dragged to is already a value the host will accept. */
export function SettingSlider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}): JSX.Element {
  return (
    <label className="settings-row">
      <span>{label}</span>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="settings-unit">{format(value)}</span>
    </label>
  );
}
